import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Mail, Lock, User, Users, Trophy, Phone, ArrowLeft, RefreshCw, CheckCircle, Eye, EyeOff } from 'lucide-react';
import { collection, getDocs, doc, setDoc, addDoc } from 'firebase/firestore';
import { sendPasswordResetEmail } from 'firebase/auth';
import { db, auth } from '../../services/firebase';
import { authService } from '../../services/authService';
import termsService from '../../services/termsService';

const Login = ({ onLogin, onSignup, onSwitchMode }) => {
  const navigate = useNavigate();
  const [mode, setMode] = useState('login');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [name, setName] = useState('');
  const [phoneNumber, setPhoneNumber] = useState('');
  const [clubCode, setClubCode] = useState('');
  const [availableClubs, setAvailableClubs] = useState([]);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [loading, setLoading] = useState(false);
  const [showForgotPassword, setShowForgotPassword] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  
  // Email verification states
  const [showVerifyEmail, setShowVerifyEmail] = useState(false);
  const [verificationEmailSent, setVerificationEmailSent] = useState(false);
  const [resendingEmail, setResendingEmail] = useState(false);
  const [pendingUser, setPendingUser] = useState(null);

  // Load available clubs when in signup mode
  useEffect(() => {
    if (mode === 'signup') {
      loadAvailableClubs();
    }
  }, [mode]);

  const loadAvailableClubs = async () => {
    try {
      console.log('🔵 Fetching clubs from Firestore...');
      
      // Get all clubs from Firestore
      const clubsRef = collection(db, 'club');
      const snapshot = await getDocs(clubsRef);
      
      console.log('✅ Found', snapshot.size, 'clubs in Firestore');
      
      const clubs = [];
      let defaultClub = null;
      
      snapshot.forEach((doc) => {
        const clubData = doc.data();
        const club = {
          id: doc.id,
          name: clubData.name,
          code: clubData.code || clubData.joinCode, // Handle both code and joinCode
          location: clubData.location
        };
        clubs.push(club);
        console.log('📋 Club:', clubData.name, '-', clubData.location);
        
        // Find Cascadia Willow Club by name only for default
        if (clubData.name === 'Cascadia Willow Club') {
          defaultClub = club;
          console.log('✅ Found Cascadia Willow Club:', club);
        }
      });
      
      setAvailableClubs(clubs);
      
      // Try to set the matching club's code if found
      if (defaultClub) {
        setClubCode(defaultClub.code);
        console.log('🔵 Defaulting to Cascadia Willow Club with code:', defaultClub.code);
      } else {
        console.log('⚠️ Cascadia Willow Club not found in clubs list');
        // Still need to handle signup, so we'll need to create the club entry
      }
      
      if (clubs.length === 0) {
        console.log('⚠️ No clubs found in Firestore');
      }
    } catch (error) {
      console.error('❌ Error loading clubs from Firestore:', error);
      console.error('❌ Error details:', error.message);
    }
  };

  // Validation helper functions
  const isValidEmail = (email) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  const isValidPhoneNumber = (phone) => {
    // Remove all non-digit characters for validation
    const digitsOnly = phone.replace(/\D/g, '');
    // Valid if 10-15 digits (covers most international formats)
    return digitsOnly.length >= 10 && digitsOnly.length <= 15;
  };

  const formatPhoneNumber = (value) => {
    // Remove all non-digit characters
    const digitsOnly = value.replace(/\D/g, '');
    
    // Format as (XXX) XXX-XXXX for US numbers
    if (digitsOnly.length <= 3) {
      return digitsOnly;
    } else if (digitsOnly.length <= 6) {
      return `(${digitsOnly.slice(0, 3)}) ${digitsOnly.slice(3)}`;
    } else if (digitsOnly.length <= 10) {
      return `(${digitsOnly.slice(0, 3)}) ${digitsOnly.slice(3, 6)}-${digitsOnly.slice(6)}`;
    } else {
      // For international numbers, just add the digits
      return `+${digitsOnly.slice(0, digitsOnly.length - 10)} (${digitsOnly.slice(-10, -7)}) ${digitsOnly.slice(-7, -4)}-${digitsOnly.slice(-4)}`;
    }
  };

  const handlePhoneChange = (e) => {
    const formatted = formatPhoneNumber(e.target.value);
    setPhoneNumber(formatted);
  };

  const handleSubmit = async (e) => {
    e?.preventDefault();
    
    console.log('🔵 Login/Signup button clicked');
    setError('');
    setSuccess('');
    setLoading(true);

    try {
      // Basic validation
      if (!email || !password) {
        setError('Please enter email and password');
        setLoading(false);
        return;
      }

      // Email validation for both login and signup
      if (!isValidEmail(email)) {
        setError('Please enter a valid email address');
        setLoading(false);
        return;
      }

      // Signup-specific validations
      if (mode === 'signup') {
        if (!name) {
          setError('Please enter your name');
          setLoading(false);
          return;
        }

        if (!phoneNumber) {
          setError('Please enter your phone number');
          setLoading(false);
          return;
        }

        if (!isValidPhoneNumber(phoneNumber)) {
          setError('Please enter a valid phone number (at least 10 digits)');
          setLoading(false);
          return;
        }

        if (!clubCode) {
          setError('Please select a club to join');
          setLoading(false);
          return;
        }

        if (password !== confirmPassword) {
          setError('Passwords do not match. Please try again.');
          setLoading(false);
          return;
        }

        if (password.length < 6) {
          setError('Password must be at least 6 characters long');
          setLoading(false);
          return;
        }
      }

      console.log('🔵 Validation passed, attempting', mode);

      let result;
      if (mode === 'signup') {
        // All signups are for players by default
        console.log('🔵 Calling onSignup with:', { email, name, phoneNumber, clubCode });
        result = await onSignup(email, name, password, 'player', clubCode, phoneNumber);
        console.log('🔵 Signup result:', result);
        
        if (result.success) {
          // Record terms acceptance
          try {
            const currentTerms = await termsService.getCurrentTerms();
            if (currentTerms) {
              await termsService.recordAcceptance(result.user.uid, currentTerms.version);
              console.log('✅ Terms acceptance recorded');
            }
          } catch (termsError) {
            console.error('⚠️ Failed to record terms acceptance:', termsError);
            // Don't block signup for this
          }
          
          // Send verification email
          console.log('📧 Sending verification email...');
          const verifyResult = await authService.sendVerificationEmail(result.user);
          
          if (verifyResult.success) {
            console.log('✅ Verification email sent');
            setPendingUser(result.user);
            setVerificationEmailSent(true);
            setShowVerifyEmail(true);
            setLoading(false);
            return; // Don't proceed further, show verification screen
          } else {
            console.error('❌ Failed to send verification email:', verifyResult.error);
            // Still show verification screen but with error
            setPendingUser(result.user);
            setShowVerifyEmail(true);
            setError(verifyResult.error);
            setLoading(false);
            return;
          }
        }
      } else {
        // Login - role determined from database
        console.log('🔵 Calling onLogin with:', { email });
        result = await onLogin(email, password);
        console.log('🔵 Login result:', result);
        
        // Note: We don't block existing users for email verification
        // Email verification is only required for NEW signups
      }

      if (!result.success) {
        console.error('❌ Authentication failed:', result.error);
        setError(result.error);
      } else {
        console.log('✅ Authentication successful');
      }
    } catch (err) {
      console.error('❌ Unexpected error in handleSubmit:', err);
      setError('An unexpected error occurred. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  // Resend verification email
  const handleResendVerification = async () => {
    setResendingEmail(true);
    setError('');
    
    try {
      const result = await authService.sendVerificationEmail(pendingUser);
      if (result.success) {
        setVerificationEmailSent(true);
        setSuccess('Verification email sent! Please check your inbox.');
      } else {
        setError(result.error);
      }
    } catch (err) {
      setError('Failed to resend verification email. Please try again.');
    } finally {
      setResendingEmail(false);
    }
  };

  // Check if email has been verified
  const handleCheckVerification = async () => {
    setLoading(true);
    setError('');
    
    try {
      const result = await authService.reloadUser();
      if (result.success && result.user.emailVerified) {
        console.log('✅ Email verified!');
        setShowVerifyEmail(false);
        // Redirect to dashboard or registration form
        window.location.reload();
      } else {
        setError('Email not yet verified. Please check your inbox and click the verification link.');
      }
    } catch (err) {
      setError('Failed to check verification status. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleForgotPassword = async () => {
    setError('');
    setSuccess('');
    
    if (!email) {
      setError('Please enter your email address');
      return;
    }

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email)) {
      setError('Please enter a valid email address');
      return;
    }

    setLoading(true);
    try {
      console.log('🔵 Attempting to send password reset email to:', email);
      
      // Use custom password reset page URL
      const actionCodeSettings = {
        url: `${window.location.origin}/reset-password`,
        handleCodeInApp: false
      };
      
      await sendPasswordResetEmail(auth, email, actionCodeSettings);
      
      console.log('✅ Password reset email sent successfully to:', email);
      console.log('✅ Check your email inbox and spam folder');
      setSuccess('Password reset email sent! Check your inbox and spam folder.');
      
      setTimeout(() => {
        setShowForgotPassword(false);
        setSuccess('');
      }, 5000);
    } catch (error) {
      console.error('❌ Error sending password reset email:', error);
      console.error('Error code:', error.code);
      console.error('Error message:', error.message);
      
      if (error.code === 'auth/user-not-found') {
        setSuccess('If an account exists with this email, a password reset link has been sent.');
        setTimeout(() => {
          setShowForgotPassword(false);
          setSuccess('');
        }, 5000);
      } else if (error.code === 'auth/invalid-email') {
        setError('Invalid email address format');
      } else if (error.code === 'auth/too-many-requests') {
        setError('Too many requests. Please try again in a few minutes.');
      } else if (error.code === 'auth/network-request-failed') {
        setError('Network error. Please check your internet connection.');
      } else {
        setError('Failed to send password reset email. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      {/* Email Verification Screen */}
      {showVerifyEmail ? (
        <div className="min-h-screen bg-gradient-to-br from-green-50 via-white to-blue-50 flex items-center justify-center p-4">
          <div className="w-full max-w-md">
            <div className="text-center mb-8">
              <Trophy className="w-12 h-12 text-green-600 mx-auto mb-3" />
              <h1 className="text-4xl font-bold mb-1">
                <span className="text-green-600">Club</span><span className="text-blue-600">Hudl</span>
              </h1>
              <p className="text-xs text-gray-500 font-medium tracking-wide uppercase">Your Club Operations Made Easy</p>
            </div>

            <div className="bg-white rounded-2xl shadow-xl p-8">
              <div className="text-center mb-6">
                <div className="inline-flex items-center justify-center w-16 h-16 bg-blue-100 rounded-full mb-4">
                  <Mail className="text-blue-600" size={32} />
                </div>
                <h2 className="text-2xl font-bold mb-4 text-gray-900">Verify Your Email</h2>
                <p className="text-gray-600">
                  We have sent a verification email to the provided email address. Please verify to proceed further.
                </p>
              </div>

              {error && (
                <div className="mb-4 p-3 bg-red-50 border border-red-200 text-red-700 rounded-lg text-sm">
                  {error}
                </div>
              )}

              {success && (
                <div className="mb-4 p-3 bg-green-50 border border-green-200 text-green-700 rounded-lg text-sm">
                  {success}
                </div>
              )}

              <div className="space-y-3">
                <button
                  onClick={handleCheckVerification}
                  disabled={loading}
                  className="w-full bg-green-600 text-white py-3 rounded-lg font-semibold hover:bg-green-700 disabled:opacity-50 transition-colors flex items-center justify-center gap-2"
                >
                  {loading ? (
                    <>
                      <RefreshCw className="animate-spin" size={18} />
                      Checking...
                    </>
                  ) : (
                    <>
                      <CheckCircle size={18} />
                      I've Verified My Email
                    </>
                  )}
                </button>

                <button
                  onClick={handleResendVerification}
                  disabled={resendingEmail}
                  className="w-full border border-gray-300 text-gray-700 py-3 rounded-lg font-semibold hover:bg-gray-50 disabled:opacity-50 transition-colors flex items-center justify-center gap-2"
                >
                  {resendingEmail ? (
                    <>
                      <RefreshCw className="animate-spin" size={18} />
                      Sending...
                    </>
                  ) : (
                    <>
                      <Mail size={18} />
                      Resend Verification Email
                    </>
                  )}
                </button>
              </div>

              <div className="mt-6 pt-6 border-t border-gray-200 text-center">
                <p className="text-sm text-gray-500 mb-3">
                  Didn't receive the email? Check your spam folder.
                </p>
                <button
                  onClick={() => {
                    setShowVerifyEmail(false);
                    setPendingUser(null);
                    setError('');
                    setSuccess('');
                  }}
                  className="text-sm text-gray-600 hover:text-gray-800"
                >
                  ← Back to Login
                </button>
              </div>
            </div>
          </div>
        </div>
      ) : (
        /* Regular Login/Signup UI */
        <div className="min-h-screen bg-gradient-to-br from-green-50 via-white to-blue-50 flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        {/* Back to Home Link */}
        <button
          onClick={() => navigate('/')}
          className="flex items-center gap-2 text-gray-600 hover:text-green-600 mb-6 transition-colors"
        >
          <ArrowLeft size={18} />
          <span className="text-sm font-medium">Back to Home</span>
        </button>

        {/* Header */}
        <div className="text-center mb-8">
          <Trophy className="w-12 h-12 text-green-600 mx-auto mb-3" />
          <h1 className="text-4xl font-bold mb-1">
            <span className="text-green-600">Club</span><span className="text-blue-600">Hudl</span>
          </h1>
          <p className="text-xs text-gray-500 font-medium tracking-wide uppercase">Your Club Operations Made Easy</p>
        </div>

        <div className="bg-white rounded-2xl shadow-xl p-8">
          {/* Mode Tabs */}
          <div className="flex gap-2 mb-6 bg-gray-100 p-1 rounded-lg">
            <button
              onClick={() => {
                setMode('login');
                setConfirmPassword('');
                setError('');
              }}
              className={`flex-1 py-2 px-4 rounded-md font-medium ${
                mode === 'login' ? 'bg-white text-green-600 shadow-sm' : 'text-gray-600'
              }`}
            >
              Login
            </button>
            <button
              onClick={() => {
                setMode('signup');
                setError('');
              }}
              className={`flex-1 py-2 px-4 rounded-md font-medium ${
                mode === 'signup' ? 'bg-white text-green-600 shadow-sm' : 'text-gray-600'
              }`}
            >
              Sign Up
            </button>
          </div>

          {/* Error/Success Messages */}
          {error && (
            <div className="mb-4 p-3 bg-red-50 border border-red-200 text-red-700 rounded-lg text-sm">
              {error}
            </div>
          )}
          {success && (
            <div className="mb-4 p-3 bg-green-50 border border-green-200 text-green-700 rounded-lg text-sm">
              {success}
            </div>
          )}

          {/* Login/Signup/Forgot Password Forms */}
          {showForgotPassword ? (
            /* Forgot Password Form */
            <div className="space-y-4">
              <div className="text-center mb-4">
                <h3 className="text-lg font-semibold mb-2">Reset Password</h3>
                <p className="text-sm text-gray-600">Enter your email to receive a password reset link</p>
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Email</label>
                <div className="relative">
                  <Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={20} />
                  <input
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className="w-full pl-10 pr-4 py-3 border rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
                    placeholder="you@example.com"
                  />
                </div>
              </div>
              <button
                onClick={handleForgotPassword}
                disabled={loading}
                className="w-full bg-green-600 text-white py-3 rounded-lg font-semibold hover:bg-green-700 disabled:opacity-50 transition-colors"
              >
                {loading ? 'Sending...' : 'Send Reset Link'}
              </button>
              <button
                onClick={() => setShowForgotPassword(false)}
                className="w-full text-gray-600 hover:text-gray-800 text-sm font-medium"
              >
                ← Back to Login
              </button>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="space-y-4">
              {/* Name field - ONLY for signup */}
              {mode === 'signup' && (
                <div>
                  <label className="block text-sm font-medium mb-1">Name *</label>
                  <div className="relative">
                    <User className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={20} />
                    <input
                      type="text"
                      value={name}
                      onChange={(e) => setName(e.target.value)}
                      className="w-full pl-10 pr-4 py-3 border rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
                      placeholder="John Doe"
                    />
                  </div>
                </div>
              )}

              {/* Phone Number field - ONLY for signup */}
              {mode === 'signup' && (
                <div>
                  <label className="block text-sm font-medium mb-1">Phone Number *</label>
                  <div className="relative">
                    <Phone className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={20} />
                    <input
                      type="tel"
                      value={phoneNumber}
                      onChange={handlePhoneChange}
                      className={`w-full pl-10 pr-4 py-3 border rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent ${
                        phoneNumber && !isValidPhoneNumber(phoneNumber) ? 'border-red-300 bg-red-50' : ''
                      }`}
                      placeholder="(123) 456-7890"
                    />
                  </div>
                  {phoneNumber && !isValidPhoneNumber(phoneNumber) && (
                    <p className="text-xs text-red-600 mt-1">Please enter a valid phone number (at least 10 digits)</p>
                  )}
                  {phoneNumber && isValidPhoneNumber(phoneNumber) && (
                    <p className="text-xs text-green-600 mt-1">✓ Valid phone number</p>
                  )}
                </div>
              )}

              {/* Email field - both login and signup */}
              <div>
                <label className="block text-sm font-medium mb-1">Email {mode === 'signup' && '*'}</label>
                <div className="relative">
                  <Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={20} />
                  <input
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className={`w-full pl-10 pr-4 py-3 border rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent ${
                      email && !isValidEmail(email) ? 'border-red-300 bg-red-50' : ''
                    }`}
                    placeholder="you@example.com"
                  />
                </div>
                {email && !isValidEmail(email) && (
                  <p className="text-xs text-red-600 mt-1">Please enter a valid email address</p>
                )}
                {mode === 'signup' && email && isValidEmail(email) && (
                  <p className="text-xs text-green-600 mt-1">✓ Valid email</p>
                )}
              </div>

              {/* Password field - both login and signup */}
              <div>
                <label className="block text-sm font-medium mb-1">Password {mode === 'signup' && '*'}</label>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={20} />
                  <input
                    type={showPassword ? "text" : "password"}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    className="w-full pl-10 pr-12 py-3 border rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
                    placeholder="••••••••"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                  >
                    {showPassword ? <EyeOff size={20} /> : <Eye size={20} />}
                  </button>
                </div>
                {mode === 'signup' && (
                  <p className="text-xs text-gray-500 mt-1">Must be at least 6 characters</p>
                )}
              </div>

              {/* Confirm Password field - ONLY for signup */}
              {mode === 'signup' && (
                <div>
                  <label className="block text-sm font-medium mb-1">Confirm Password *</label>
                  <div className="relative">
                    <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={20} />
                    <input
                      type={showConfirmPassword ? "text" : "password"}
                      value={confirmPassword}
                      onChange={(e) => setConfirmPassword(e.target.value)}
                      className={`w-full pl-10 pr-12 py-3 border rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent ${
                        confirmPassword && password !== confirmPassword ? 'border-red-300 bg-red-50' : ''
                      }`}
                      placeholder="••••••••"
                    />
                    <button
                      type="button"
                      onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                      className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                    >
                      {showConfirmPassword ? <EyeOff size={20} /> : <Eye size={20} />}
                    </button>
                  </div>
                  {confirmPassword && password !== confirmPassword && (
                    <p className="text-xs text-red-600 mt-1">Passwords do not match</p>
                  )}
                  {confirmPassword && password === confirmPassword && confirmPassword.length >= 6 && (
                    <p className="text-xs text-green-600 mt-1">✓ Passwords match</p>
                  )}
                </div>
              )}

              {/* Club Selection - OPTIONAL for signup */}
              {mode === 'signup' && (
                <div>
                  <label className="block text-sm font-medium mb-2">
                    Select Your Club *
                  </label>
                  {availableClubs.length === 0 ? (
                    <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg">
                      <p className="text-sm text-blue-800">
                        ℹ️ Loading club information...
                      </p>
                    </div>
                  ) : (
                    <>
                      <div className="relative">
                        <Users className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 z-10" size={20} />
                        <select
                          value={clubCode}
                          onChange={(e) => setClubCode(e.target.value)}
                          className="w-full pl-10 pr-10 py-3 border rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent appearance-none bg-white text-gray-900"
                        >
                          <option value="" disabled>Select a club to join</option>
                          {availableClubs.map(club => (
                            <option key={club.id} value={club.code}>
                              {club.name} {club.location ? `- ${club.location}` : ''}
                            </option>
                          ))}
                        </select>
                        <div className="absolute inset-y-0 right-0 flex items-center pr-3 pointer-events-none">
                          <svg className="w-5 h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                          </svg>
                        </div>
                      </div>
                      {!clubCode && (
                        <p className="text-xs text-red-600 mt-2">
                          Please select a club to join.
                        </p>
                      )}
                    </>
                  )}
                </div>
              )}

              {/* Terms of Service Notice - ONLY for signup */}
              {mode === 'signup' && (
                <div className="text-center mt-4 mb-4">
                  <p className="text-sm text-gray-600">
                    By signing up to use ClubHudl, you accept the{' '}
                    <a
                      href="/terms"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-blue-600 hover:text-blue-700 underline font-medium"
                    >
                      Terms and Conditions
                    </a>
                  </p>
                </div>
              )}

              {/* Submit Button */}
              <button
                type="submit"
                disabled={loading}
                className="w-full bg-green-600 text-white py-3 rounded-lg font-semibold hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                {loading ? 'Please wait...' : mode === 'signup' ? 'Create Account' : 'Sign In'}
              </button>

              {/* Forgot Password Link - ONLY for login */}
              {mode === 'login' && (
                <div className="text-center">
                  <button
                    type="button"
                    onClick={() => setShowForgotPassword(true)}
                    className="text-sm text-green-600 hover:text-green-700 font-medium"
                  >
                    Forgot Password?
                  </button>
                </div>
              )}
            </form>
          )}
        </div>
      </div>
      </div>
      )}
    </>
  );
};

export default Login;